import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { chatApi, type ChatContact, type Conversation } from '@/api/chat.api';
import { useAuth } from '@/contexts/AuthContext';
import { useCan } from '@/hooks/useCan';
import { chatSocketService } from '@/services/chatSocket.service';
import { playNotificationSound } from '@/utils/notificationSound';
import { clearAllTyping, setTyping } from '@/domain/chat-typing';
import {
  applyMessageEdit,
  badgeCount,
  chatKeys,
  isMutedNow,
  markLastMessageDeleted,
  markMessageDeleted,
  mentionsMe,
  removeConversation,
  setConversationMute,
  setConversationRead,
  touchConversation,
  upsertMessage,
} from '@/domain/chat-cache';

interface ChatContextType {
  /** The user has a staff record here and a role that includes chat. */
  enabled: boolean;
  staffId: string | null;
  unreadTotal: number;
  isOnline: (staffId: string) => boolean;
  /**
   * The conversation on screen, or null. Messages arriving in it are read
   * as they land instead of counting as unread and chiming.
   */
  setActiveConversation: (conversationId: string | null) => void;
  markRead: (conversationId: string) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

/** The sidebar badge: unread messages, less what a mute holds back. */
const sumUnread = (list: Conversation[]) => {
  const now = Date.now();
  return list.reduce((sum, c) => sum + badgeCount(c, now), 0);
};

const isWatching = () =>
  document.visibilityState === 'visible' && document.hasFocus();

export function ChatProvider({ children }: { children: ReactNode }) {
  const { user, clinic } = useAuth();
  const { canView } = useCan();
  const queryClient = useQueryClient();

  const staffId = user?.staffId ?? null;
  const clinicId = clinic?.id ?? null;
  const enabled = !!staffId && !!clinicId && canView('chat');

  const activeRef = useRef<string | null>(null);

  // The list is the source of the sidebar badge, so it is held at the app
  // root rather than fetched when the Chat page opens.
  const { data: unreadTotal = 0 } = useQuery({
    queryKey: chatKeys.conversations,
    queryFn: chatApi.getConversations,
    enabled,
    staleTime: 60_000,
    select: sumUnread,
  });

  const { data: contactsData } = useQuery({
    queryKey: chatKeys.contacts,
    queryFn: chatApi.getContacts,
    enabled,
    staleTime: 5 * 60_000,
  });
  // Presence rides on the contacts query: the fetch seeds who is online and
  // each `chat:presence` frame patches that same cache entry.
  const online = useMemo(
    () => new Set(contactsData?.online ?? []),
    [contactsData],
  );

  const markRead = useCallback(
    (conversationId: string) => {
      if (!staffId) return;
      setConversationRead(
        queryClient,
        conversationId,
        staffId,
        new Date().toISOString(),
        true,
      );
      // The count is already zero on screen; a failed call corrects itself
      // on the next list fetch.
      chatApi.markRead(conversationId).catch(() => undefined);
    },
    [queryClient, staffId],
  );

  const setActiveConversation = useCallback((conversationId: string | null) => {
    activeRef.current = conversationId;
  }, []);

  useEffect(() => {
    if (!enabled || !staffId) {
      chatSocketService.disconnect();
      return;
    }
    // `reconnect`, not `connect`: a clinic switch re-issues the cookie.
    chatSocketService.reconnect();

    const offs = [
      chatSocketService.on('chat:message', ({ message }) => {
        const mine = message.senderId === staffId;
        setTyping(message.conversationId, message.senderId, false);
        upsertMessage(queryClient, message);

        const reading =
          !mine && activeRef.current === message.conversationId && isWatching();
        const forMe = mentionsMe(message, staffId);
        const conversation = queryClient
          .getQueryData<Conversation[]>(chatKeys.conversations)
          ?.find((c) => c.id === message.conversationId);
        const known = touchConversation(queryClient, message, {
          countAsUnread: !mine && !reading,
          mentionsMe: forMe,
        });
        if (!known) {
          void queryClient.invalidateQueries({
            queryKey: chatKeys.conversations,
          });
        }
        if (mine) return;
        if (reading) {
          markRead(message.conversationId);
          return;
        }
        // An urgent message also arrives as a notification, which has its
        // own toast and sound; a second chime for it is noise.
        if (message.priority === 'urgent') return;
        // A message naming me also arrives as a notification, with its own
        // toast and sound (it must reach me even with the chat muted).
        if (forMe) return;
        if (conversation && isMutedNow(conversation)) return;
        try {
          playNotificationSound();
        } catch {
          /* no audio device, or blocked until the user interacts */
        }
      }),

      chatSocketService.on('chat:message.deleted', (event) => {
        markMessageDeleted(queryClient, event.conversationId, event.messageId);
        markLastMessageDeleted(
          queryClient,
          event.conversationId,
          event.messageId,
        );
        // Removing an unread message changes the count; ask rather than guess.
        void queryClient.invalidateQueries({
          queryKey: chatKeys.conversations,
        });
      }),

      chatSocketService.on('chat:message.updated', ({ message }) => {
        applyMessageEdit(queryClient, message);
      }),

      chatSocketService.on('chat:mute', (event) => {
        setConversationMute(
          queryClient,
          event.conversationId,
          event.muted,
          event.mutedUntil,
        );
      }),

      chatSocketService.on('chat:read', (event) => {
        setConversationRead(
          queryClient,
          event.conversationId,
          event.staffId,
          event.lastReadAt,
          event.staffId === staffId,
        );
      }),

      chatSocketService.on('chat:typing', (event) => {
        if (event.staffId === staffId) return;
        setTyping(event.conversationId, event.staffId, event.isTyping);
      }),

      chatSocketService.on('chat:conversation', () => {
        void queryClient.invalidateQueries({
          queryKey: chatKeys.conversations,
        });
      }),

      chatSocketService.on('chat:conversation.removed', (event) => {
        removeConversation(queryClient, event.conversationId);
      }),

      chatSocketService.on('chat:presence', (event) => {
        queryClient.setQueryData<{ contacts: ChatContact[]; online: string[] }>(
          chatKeys.contacts,
          (current) => {
            if (!current) return current;
            const has = current.online.includes(event.staffId);
            if (has === event.online) return current;
            return {
              ...current,
              online: event.online
                ? [...current.online, event.staffId]
                : current.online.filter((id) => id !== event.staffId),
            };
          },
        );
      }),

      // Whatever was sent while the socket was down was missed, and the
      // socket carries no history: refetch what is on screen.
      chatSocketService.onReconnect(() => {
        void queryClient.invalidateQueries({ queryKey: chatKeys.all });
      }),
    ];

    return () => {
      for (const off of offs) off();
      chatSocketService.disconnect();
      clearAllTyping();
    };
  }, [enabled, staffId, clinicId, queryClient, markRead]);

  // Another clinic's (or a signed-out user's) conversations must not linger:
  // the cache is dropped whenever the person or the clinic changes.
  useEffect(
    () => () => queryClient.removeQueries({ queryKey: chatKeys.all }),
    [staffId, clinicId, queryClient],
  );

  const value = useMemo<ChatContextType>(
    () => ({
      enabled,
      staffId,
      unreadTotal: enabled ? unreadTotal : 0,
      isOnline: (id: string) => online.has(id),
      setActiveConversation,
      markRead,
    }),
    [enabled, staffId, unreadTotal, online, setActiveConversation, markRead],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat(): ChatContextType {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return ctx;
}
